import { useCallback, useEffect, useMemo, useState } from 'react';
import { Combobox, Field, Option } from '@fluentui/react-components';
import { fieldMeetCertainCriteriaStyles } from '../styles';
import { useAutomationRuleConditions } from '../../use-conditions';
import { ConditionItemType, FieldMeetCertainCriteriaType } from '../../types';
import { useOperatorsHook } from './use-operators';
import ClearebleButtonInput from 'src/components/shared/cleareble-button-input';

interface Props {
  id?: string;
  conditionId: string;
  validationError: string | null;
}

export default function OperatorInput(props: Props) {
	const classes = fieldMeetCertainCriteriaStyles();
	const operatorsHook = useOperatorsHook();

	const {conditions, setConditions, resetValidationErrors} = useAutomationRuleConditions();

	const [options, setOptions] = useState<Array<{ value: string; label: string }>>([]);
	
	const [status, setStatus] = useState<'searching' | 'selected'>('selected');
	const [text, setText] = useState<string>('');

	const current: FieldMeetCertainCriteriaType = useMemo(() => {
		const foundConditions = conditions.find(item => item.id === props.conditionId) as ConditionItemType;
		return foundConditions!.fieldMeetCertainCriteria as FieldMeetCertainCriteriaType;
	}, [conditions, props.conditionId]);

	const onChangeText = useCallback((value: string) => {
		setStatus('searching');
		setText(value);
		
		if (value === '') {
			operatorsHook.getOperatorOptions(current.workItemField).then((resultOptions) => {
				setOptions(resultOptions);
			});
		}
		
		if (value !== '') {
			const filteredOptions = options.filter(item => item.label.toLowerCase().includes(value.toLowerCase()));
			setOptions(filteredOptions);
		}
	}, [options]);

	const setOptionSelect = useCallback((value: string) => {
		if (value) {
			resetValidationErrors();
			const foundCondition = conditions.find(item => item.id === props.conditionId);
			setStatus('selected');
	
			if (foundCondition) {
				setConditions(oldConditions => {
					const current = oldConditions.find(item => item.id === props.conditionId) as ConditionItemType;
					const others = oldConditions.filter(item => item.id !== props.conditionId);
	
					return [...others, {
						...current, fieldMeetCertainCriteria: {
							...current.fieldMeetCertainCriteria as FieldMeetCertainCriteriaType,
							operator: value,
							userFieldValue: [],
							iterationOrAreaPathValue: '',
							textValue: '',
							selectValue: [],
							dateTimeValue: {
								type: 'datetime',
								datetime: {
									value: new Date(),
								},
								timeframe: {
									timeNumber: 0,
									timeUnit: 'minutes',
									timeOption: 'ago',
								},
							}
						}
					}];
				});
			}
		}
	}, []);

	const clearOptionSelect = useCallback(() => {
		const foundCondition = conditions.find(item => item.id === props.conditionId);
		setStatus('selected');

		if (foundCondition) {
			setConditions(oldConditions => {
				const current = oldConditions.find(item => item.id === props.conditionId) as ConditionItemType;
				const others = oldConditions.filter(item => item.id !== props.conditionId);
	
				return [...others, {
					...current, fieldMeetCertainCriteria: {
						...current.fieldMeetCertainCriteria as FieldMeetCertainCriteriaType,
						operator: '',
						dateTimeValue: {
							type: 'datetime',
							datetime: {
								value: new Date(),
							},
							timeframe: {
								timeNumber: 0,
								timeUnit: 'minutes',
								timeOption: 'ago',
							},
						},
						userFieldValue: [],
						iterationOrAreaPathValue: '',
					}
				}];
			});
		}
	}, []);
  
	const valueRender = useMemo(() => {
		if (current && current.operator && status === 'selected') {
			return options.find(item => item.value === current.operator)?.label || '';
		}

		if (text) {
			return text;
		}

		return '';
	}, [text, current, options, status]);

	const selectedOptions = useMemo(() => {
		const found = options.find(item => item.value === current.operator);
		if (current.operator && found) {
			return [found.value];
		}

		return [];
	}, [current, options]);

	const getOperatorOptionsAsync = useCallback(async () => {
		const resultOptions = await operatorsHook.getOperatorOptions(current.workItemField);
		setOptions(resultOptions);
	}, [current.workItemField]);

	useEffect(() => {
		getOperatorOptionsAsync();
	}, []);
  
	return (
		<Field
			className={classes.dropdownRoot}
			validationMessage={props.validationError}
		>
			<Combobox
				clearable
				clearIcon={(
					<ClearebleButtonInput onClick={clearOptionSelect} />
				)}
				inputMode='search'
				autoComplete='off'
				className={classes.dropdown}
				appearance='outline'
				placeholder='Choose a value'
				positioning="below-start"
				value={valueRender}
				selectedOptions={selectedOptions}
				onOptionSelect={(_, data) => {
					setOptionSelect(data.optionValue as string);
				}}
				onChange={(ev) => {
					onChangeText(ev.target.value);
				}}
				onBlur={() => {
					setStatus('selected');
					getOperatorOptionsAsync();
				}}
			>
				<div style={{maxHeight: 240}}>
					{options.map((item, idx) => (
						<Option key={idx} value={item.value}>{item.label}</Option>
					))}
				</div>
			</Combobox>
		</Field>
	);
}